import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SyncIcon from '@mui/icons-material/Sync';
import {
  AccountingIntegrationResponse,
  FortnoxValidationResult,
  SendTestVoucherResponse,
  VoucherPreviewResponse,
  disconnectIntegration,
  getFortnoxAuthUrl,
  getIntegrationsByOwnerId,
  getVoucherPreview,
  sendTestVoucher,
  updateSchedule,
  validateIntegration,
} from 'src/shared/services/accountingIntegrationService';
import { FortnoxSettingsModal } from './fortnoxSettingsModal';

type AccountingFrequency = 'off' | 'daily' | 'monthly';

interface AvailableIntegration {
  id: string;
  nameKey: string;
  connectKey: string;
}

const AVAILABLE_INTEGRATIONS: AvailableIntegration[] = [
  {
    id: 'fortnox',
    nameKey: 'fortnox',
    connectKey: 'connectFortnox',
  },
  // {
  //   id: 'visma',
  //   nameKey: 'visma',
  //   connectKey: 'connectVisma',
  // },
];

export const AdminInvoiceGroupAccountingIntegrationPanel = () => {
  const { t } = useTranslation(['invoice', 'common']);
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerId = params.id ? Number(params.id) : 0;
  const autoOpenDone = useRef(false);

  const [integrations, setIntegrations] = useState<AccountingIntegrationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [pendingFrequency, setPendingFrequency] = useState<AccountingFrequency>('off');

  // Schemaläggning
  const [startDateTime, setStartDateTime] = useState<Dayjs | null>(dayjs().add(1, 'day').hour(3).minute(0));
  const [hasExistingTask, setHasExistingTask] = useState(false);
  const [nextRunDisplay, setNextRunDisplay] = useState('');
  const [filterByDate, setFilterByDate] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState<Dayjs | null>(dayjs());
  const [voucherGrouping, setVoucherGrouping] = useState<'daily' | 'monthly'>('daily');

  // Validering
  const [validationData, setValidationData] = useState<FortnoxValidationResult | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);

  // Preview
  const [previewDate, setPreviewDate] = useState<Dayjs | null>(dayjs());
  const [previewData, setPreviewData] = useState<VoucherPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Testverifikat
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [sendTestResult, setSendTestResult] = useState<SendTestVoucherResponse | null>(null);

  const fetchIntegrations = useCallback(async () => {
    if (ownerId <= 0) return;
    try {
      const result = await getIntegrationsByOwnerId(ownerId);
      setIntegrations(result.data);
    } catch {
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Öppna modalen automatiskt efter Fortnox-callback
  useEffect(() => {
    if (autoOpenDone.current || loading) return;
    if (searchParams.get('openSettings') === 'true' && integrations.find((i) => i.isActive === 1)) {
      autoOpenDone.current = true;
      searchParams.delete('openSettings');
      setSearchParams(searchParams, { replace: true });
      handleOpenSettings(); // eslint-disable-line @typescript-eslint/no-use-before-define
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, integrations, searchParams]);

  const getActiveIntegration = (provider: string): AccountingIntegrationResponse | undefined => {
    return integrations.find((i) => i.provider === provider && i.isActive === 1);
  };

  const connectedProvider = integrations.find((i) => i.isActive === 1)?.provider ?? null;

  const handleConnect = async (provider: string) => {
    if (provider === 'fortnox') {
      try {
        const result = await getFortnoxAuthUrl(ownerId);
        window.location.href = result.data.url;
      } catch {
        toast.error(t('invoice:accountingIntegration.connectError') as string);
      }
    }
  };

  const handleDisconnect = async () => {
    if (!connectedProvider) return;
    try {
      await disconnectIntegration(ownerId, connectedProvider);
      toast.success(t('invoice:accountingIntegration.disconnectSuccess') as string);
      setSettingsModalOpen(false);
      fetchIntegrations();
    } catch {
      toast.error(t('invoice:accountingIntegration.disconnectError') as string);
    }
  };

  const scheduleTypeToFrequency = (type: number | null | undefined): AccountingFrequency => {
    if (type === 3) return 'daily';
    if (type === 5) return 'monthly';
    return 'off';
  };

  const handleOpenSettings = () => {
    const active = integrations.find((i) => i.isActive === 1);
    const freq = scheduleTypeToFrequency(active?.scheduleType);
    setPendingFrequency(freq);
    setHasExistingTask(active?.scheduleType != null);
    setNextRunDisplay(active?.nextRun ? new Date(active.nextRun).toLocaleString('sv-SE') : '');

    setStartDateTime(dayjs().add(1, 'day').hour(3).minute(0));

    setFilterByDate(!!active?.fromDate);
    setFilterFromDate(active?.fromDate ? dayjs(active.fromDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')) : dayjs());
    setVoucherGrouping((active?.voucherGrouping as 'daily' | 'monthly') || 'daily');
    setPreviewData(null);
    setValidationData(null);
    setSendTestResult(null);
    setSettingsModalOpen(true);
    runValidation(); // eslint-disable-line @typescript-eslint/no-use-before-define
  };

  const runValidation = async () => {
    if (!connectedProvider || !previewDate) return;
    const dateFormatted = previewDate.format('YYYYMMDD');
    setValidationLoading(true);
    setValidationData(null);
    try {
      const result = await validateIntegration(ownerId, connectedProvider, dateFormatted);
      setValidationData(result.data);
    } catch {
      toast.error(t('invoice:accountingIntegration.validateError') as string);
    } finally {
      setValidationLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!connectedProvider) return;
    try {
      const fromDate = filterByDate && filterFromDate ? filterFromDate.format('YYYYMMDD') : undefined;
      const startDt = pendingFrequency !== 'off' && startDateTime ? startDateTime.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      await updateSchedule(ownerId, connectedProvider, pendingFrequency, startDt, fromDate, pendingFrequency !== 'off' ? voucherGrouping : undefined);
      toast.success(t('invoice:accountingIntegration.settingsSaved') as string);
      setSettingsModalOpen(false);
      fetchIntegrations();
    } catch {
      toast.error(t('invoice:accountingIntegration.settingsError') as string);
    }
  };

  const handleSendTest = async (date?: string) => {
    if (!connectedProvider) return;
    setSendTestLoading(true);
    setSendTestResult(null);
    try {
      const result = await sendTestVoucher(ownerId, connectedProvider, voucherGrouping, date);
      setSendTestResult(result.data);
    } catch {
      toast.error(t('invoice:accountingIntegration.sendTestError') as string);
    } finally {
      setSendTestLoading(false);
    }
  };

  const handlePreview = async (date?: string) => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const result = await getVoucherPreview(ownerId, connectedProvider || 'fortnox', voucherGrouping, date);
      setPreviewData(result.data);
    } catch {
      toast.error(t('invoice:accountingIntegration.previewError') as string);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) return null;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon color='primary' fontSize='small' />
            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
              {t('invoice:accountingIntegration.title')}
            </Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            {t('invoice:accountingIntegration.description')}
          </Typography>
        </Grid>

        {AVAILABLE_INTEGRATIONS.map((integration) => {
          const active = getActiveIntegration(integration.id);
          const isConnected = !!active;
          const isOtherConnected = connectedProvider !== null && connectedProvider !== integration.id;

          if (isOtherConnected) return null;

          return (
            <Grid item xs={12} key={integration.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {integration.id === 'fortnox' && <img src='https://www.fortnox.se/favicon.ico' alt='Fortnox' width={18} height={18} />}
                <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                  {t(`invoice:accountingIntegration.${integration.nameKey}`)}
                </Typography>
              </Box>
              <Alert
                icon={false}
                severity={isConnected ? 'success' : 'info'}
                sx={{
                  '& .MuiAlert-message': { width: '100% !important' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {isConnected ? t('invoice:accountingIntegration.connectedStatus') : t('invoice:accountingIntegration.notConnectedStatus')}

                  {isConnected ? (
                    <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={handleOpenSettings}>
                      {t('invoice:accountingIntegration.settingsButton')}
                    </Button>
                  ) : (
                    <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={() => handleConnect(integration.id)}>
                      {t(`invoice:accountingIntegration.${integration.connectKey}`)}
                    </Button>
                  )}
                </Box>
              </Alert>
            </Grid>
          );
        })}
      </Grid>

      {/* Inställningsmodal */}
      <FortnoxSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        onSave={handleSaveSettings}
        onDisconnect={handleDisconnect}
        pendingFrequency={pendingFrequency}
        setPendingFrequency={setPendingFrequency}
        hasExistingTask={hasExistingTask}
        nextRunDisplay={nextRunDisplay}
        startDateTime={startDateTime}
        setStartDateTime={setStartDateTime}
        filterByDate={filterByDate}
        setFilterByDate={setFilterByDate}
        filterFromDate={filterFromDate}
        setFilterFromDate={setFilterFromDate}
        voucherGrouping={voucherGrouping}
        setVoucherGrouping={setVoucherGrouping}
        validationData={validationData}
        validationLoading={validationLoading}
        runValidation={runValidation}
        previewDate={previewDate}
        setPreviewDate={setPreviewDate}
        previewData={previewData}
        previewLoading={previewLoading}
        handlePreview={handlePreview}
        handleSendTest={handleSendTest}
        sendTestLoading={sendTestLoading}
        sendTestResult={sendTestResult}
      />
    </>
  );
};
